<template>
  <section v-if="categories.length" :class="$style.section">
    <h2 :class="$style.title">Выберите услуги</h2>

    <div :class="[$style.types, { [$style.busy]: isActionPending }]">
      <div
        v-for="(category, index) in categories"
        :key="category.category"
        :class="[
          $style.type,
          { [$style.active]: selectedCategoryIndex === index },
        ]"
        @click="!isActionPending && (selectedCategoryIndex = index)"
      >
        {{ category.category }}
      </div>
    </div>

    <SectionLoader :active="isActionPending">
      <div :class="[$style.services, { [$style.busy]: isActionPending }]">
        <div
          v-for="complex in categories[selectedCategoryIndex].complexes"
          :key="complex.id"
          :class="[
            $style.service,
            { [$style.active]: selectedComplexId === complex.id },
          ]"
          @click="selectComplex(complex)"
        >
          <div :class="$style.top">
            <div :class="$style.name">{{ complex.title }}</div>
            <div v-if="complex.execution_time" :class="$style.time">
              <span>{{ getExecutionTimeInMinutes(complex.execution_time) }}</span>
              мин
            </div>
          </div>

          <div :class="$style.list">
            <div
              v-for="offer in complex.offers"
              :key="offer.offer.id"
              :class="$style.item"
            >
              <img
                :src="
                  selectedComplexId === complex.id
                    ? '/icons/check-green.svg'
                    : '/icons/check-grey.svg'
                "
                alt=""
              />
              {{ offer.offer.title }}
            </div>
          </div>

          <div :class="$style.bottom">
            <div :class="$style.qty">
              <span :class="$style.minus" @click.stop="onDecrementQty(complex)"
                ><img src="/icons/minus.svg" alt=""
              /></span>
              <span :class="$style.value">{{
                complex.qty ?? complex.offers[0].default_wheels_quan
              }}</span>
              <span :class="$style.plus" @click.stop="onIncrementQty(complex)"
                ><img src="/icons/plus.svg" alt=""
              /></span>
            </div>
            <div :class="$style.price">
              от
              <span :class="$style.currency"
                >{{ Math.round(getComplexDisplayPrice(complex))
                }}{{ currencySymbol }}</span
              >
            </div>
          </div>
        </div>
      </div>
    </SectionLoader>
  </section>
</template>

<script>
import { mapState } from "vuex";
import SectionLoader from "@/components/common/SectionLoader.vue";
import asyncActionMixin from "@/mixins/asyncActionMixin";
import {
  buildOfferWheelQuantityMap,
  getOffersTotalWheelQuantity,
  getOfferWheelQuantityByTire,
} from "@/utils/complexOffers";

export default {
  name: "ServicesSection",
  mixins: [asyncActionMixin],
  components: {
    SectionLoader,
  },
  data() {
    return {
      selectedCategoryIndex: 0,
      selectedComplexId: null,
    };
  },
  computed: {
    ...mapState({
      categories: (state) => state.complexes.complexes,
      order: (state) => state.orders.order,
      offerSettings: (state) => state.orders.offerSettings,
    }),
    currencySymbol() {
      return this.order?.currency?.symbol || "₽";
    },
  },
  watch: {
    order: {
      immediate: true,
      handler(value) {
        if (value?.complex) {
          this.selectedComplexId = value.complex;
        }
        this.syncSelectedComplexQty();
      },
    },
    categories: {
      deep: true,
      handler() {
        this.syncSelectedComplexQty();
      },
    },
  },
  methods: {
    getExecutionTimeInMinutes(value) {
      if (value == null || value === "") {
        return "";
      }

      if (typeof value === "number") {
        return value;
      }

      if (!String(value).includes(":")) {
        return value;
      }

      const [hours = "0", minutes = "0", seconds = "0"] =
        String(value).split(":");

      return Math.floor(
        Number(hours || 0) * 60 +
          Number(minutes || 0) +
          Number(seconds || 0) / 60
      );
    },
    normalizeEntityId(value) {
      if (!value) return null;
      return typeof value === "object" ? value.id || null : value;
    },
    getSelectedComplex() {
      return this.categories
        .flatMap((category) => category.complexes || [])
        .find(
          (complex) => String(complex.id) === String(this.selectedComplexId)
        );
    },
    getComplexOfferIds(complex) {
      return (complex?.offers || [])
        .map((item) => this.normalizeEntityId(item.offer))
        .filter(Boolean);
    },
    getComplexOrderQty(complex) {
      const complexOfferIds = this.getComplexOfferIds(complex);
      if (!complexOfferIds.length) {
        return complex?.qty ?? complex?.offers?.[0]?.default_wheels_quan ?? 1;
      }

      const orderOffers = (this.order?.offers || []).filter((item) =>
        complexOfferIds.includes(this.normalizeEntityId(item.offer))
      );
      const totalQuantity = getOffersTotalWheelQuantity(orderOffers);

      return (
        (totalQuantity || complex?.qty) ??
        complex?.qty ??
        complex?.offers?.[0]?.default_wheels_quan ??
        1
      );
    },
    getComplexOrderOffers(complex) {
      const complexOfferIds = this.getComplexOfferIds(complex);
      if (!complexOfferIds.length) {
        return [];
      }

      return (this.order?.offers || []).filter((item) =>
        complexOfferIds.includes(this.normalizeEntityId(item.offer))
      );
    },
    getComplexDisplayPrice(complex) {
      if (String(this.selectedComplexId) !== String(complex?.id)) {
        return Number(complex?.price || 0);
      }

      const orderOffers = this.getComplexOrderOffers(complex);
      if (!orderOffers.length) {
        return Number(complex?.price || 0);
      }

      const orderPrice = Number(orderOffers[0]?.order_price);
      if (!Number.isNaN(orderPrice) && orderPrice > 0) {
        return orderPrice;
      }

      const totalPrice = orderOffers.reduce(
        (sum, item) => sum + Number(item?.price || item?.offer?.price || 0),
        0
      );

      return totalPrice || Number(complex?.price || 0);
    },
    syncSelectedComplexQty() {
      const complex = this.getSelectedComplex();
      if (!complex) {
        return;
      }

      complex.qty = this.getComplexOrderQty(complex);
    },
    buildComplexOfferPayload(complex, quantity = null) {
      const complexOfferIds = this.getComplexOfferIds(complex);
      if (!complexOfferIds.length) {
        return [];
      }

      const wheelsQuan = Math.max(
        1,
        Number(quantity ?? (this.getComplexOrderQty(complex) || 1))
      );
      const includeRunflat = Boolean(this.offerSettings?.include_runflat);
      const includePressureSensor = Boolean(
        this.offerSettings?.include_pressure_sensor
      );
      const includeAlloyWheel = Boolean(
        this.offerSettings?.include_alloy_wheel
      );
      const orderOffers = (this.order?.offers || []).filter((item) =>
        complexOfferIds.includes(this.normalizeEntityId(item.offer))
      );
      const quantityMap = buildOfferWheelQuantityMap(orderOffers, wheelsQuan);

      return orderOffers
        .map((item) => {
          const offerId = this.normalizeEntityId(item.offer);
          const complexOffer = (complex?.offers || []).find(
            (entry) => this.normalizeEntityId(entry.offer) === offerId
          );
          const payload = {
            id: item.id,
            offer: offerId,
            tire: this.normalizeEntityId(item.tire),
            wheels_quan: getOfferWheelQuantityByTire(
              item,
              quantityMap,
              orderOffers.indexOf(item)
            ),
          };

          if (complexOffer?.offer?.allow_runflat) {
            payload.include_runflat = includeRunflat;
          }

          if (complexOffer?.offer?.allow_pressure_sensor) {
            payload.include_pressure_sensor = includePressureSensor;
          }

          if (complexOffer?.offer?.allow_alloy_wheel) {
            payload.include_alloy_wheel = includeAlloyWheel;
          }

          return payload;
        })
        .filter((item) => item.id && item.offer && item.tire);
    },
    async patchComplexOffers(complex, quantity = null) {
      const payload = this.buildComplexOfferPayload(complex, quantity);
      if (!payload.length) {
        return { value: [], error: null };
      }

      const result = await this.$store.dispatch("orders/patchOffers", {
        payload,
      });
      if (!result.error) {
        complex.qty =
          getOffersTotalWheelQuantity(result.value) ||
          getOffersTotalWheelQuantity(payload) ||
          quantity;
      }
      return result;
    },
    async ensureComplexSelected(complex) {
      if (String(this.selectedComplexId) === String(complex?.id)) {
        return { value: this.order, error: null };
      }

      return this.setComplex("complex", complex);
    },
    selectComplex(complex) {
      if (this.isActionPending) {
        return;
      }
      return this.withAction(() => this.setComplex("complex", complex));
    },
    onIncrementQty(complex) {
      if (this.isActionPending) {
        return;
      }
      return this.withAction(() => this.incrementQty(complex));
    },
    onDecrementQty(complex) {
      if (this.isActionPending) {
        return;
      }
      return this.withAction(() => this.decrementQty(complex));
    },
    async incrementQty(complex) {
      const { error } = await this.ensureComplexSelected(complex);
      if (error) {
        return;
      }

      const nextQty = this.getComplexOrderQty(complex) + 1;
      complex.qty = nextQty;
      await this.patchComplexOffers(complex, nextQty);
    },
    async decrementQty(complex) {
      const { error } = await this.ensureComplexSelected(complex);
      if (error) {
        return;
      }

      const currentQty = this.getComplexOrderQty(complex);
      if (currentQty <= 1) {
        return;
      }

      const nextQty = currentQty - 1;
      complex.qty = nextQty;
      await this.patchComplexOffers(complex, nextQty);
    },
    async setComplex(field, value) {
      const currentComplexId = this.normalizeEntityId(this.order?.complex);
      const nextComplexId = value.id;
      const isAnotherComplex =
        currentComplexId && String(currentComplexId) !== String(nextComplexId);

      this.selectedComplexId = nextComplexId;
      this.$store.commit("orders/setOrderField", {
        field,
        value: nextComplexId,
      });

      if (isAnotherComplex) {
        const cleared = await this.$store.dispatch("orders/clearOrderOffers");
        if (cleared?.error) {
          return cleared;
        }
      }

      const result = await this.$store.dispatch("orders/saveOrder", {
        includeComplex: true,
      });
      if (result.error) {
        return result;
      }

      value.qty = this.getComplexOrderQty(value);
      await this.patchComplexOffers(value, value.qty);
      return result;
    },
  },
};
</script>

<style lang="scss" module>
.section {
  padding: 1.5rem 1.5rem 0;
  @include tablet {
    padding: 1rem;
  }
  .title {
    font-size: 1.3125rem;
    font-weight: 600;
    margin: 0 0 1.5rem;
    line-height: 1.4;
  }
    .types {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
    &.busy {
      pointer-events: none;
    }
    .type {
      cursor: pointer;
      min-height: 2.5rem;
      padding: 0.5rem 1rem;
      border: 1px solid $bg-secondary;
      color: $t-secondary;
      border-radius: 0.25rem;
      background-color: $bg-white;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 0.875rem;
      line-height: 1.2;
    }
    .active {
      background-color: $t-primary;
      color: $t-white;
      border-color: $t-primary;
    }
  }
  .services {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
    &.busy {
      pointer-events: none;
    }
    @include custom(540) {
      grid-template-columns: 1fr;
      gap: 1rem;
    }
    .service {
      padding: 1rem;
      border: 1px solid $bg-secondary;
      background-color: $bg-primary;
      border-radius: 0.25rem;
      color: $t-secondary;
      transition: all 0.3s ease;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      min-height: 11.5rem;
      .top {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 0.5rem;
        margin-bottom: 1rem;
        .name {
          font-weight: 600;
          font-size: 1rem;
          line-height: 1.2;
        }
        .time {
          font-size: 0.75rem;
          color: inherit;
          line-height: 1.2;
          white-space: nowrap;
          span {
            color: inherit;
            font-weight: 600;
            font-size: 1.25rem;
          }
        }
      }
      .list {
        flex: 1 1 auto;
        margin-bottom: 1rem;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        .item {
          display: flex;
          align-items: flex-start;
          gap: 0.375rem;
          font-size: 0.75rem;
          line-height: 1.3;

          img {
            width: 1rem;
            height: 1rem;
            flex-shrink: 0;
            margin-top: 0.0625rem;
          }
        }
      }
      .bottom {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
        .qty {
          display: flex;
          align-items: center;
          gap: 0.5rem;

          .minus,
          .plus {
            cursor: pointer;
            width: 2rem;
            height: 2rem;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background-color: transparent;
            border: none;

            img {
              width: 1rem;
              height: 1rem;
            }
          }
          .value {
            font-weight: 600;
            font-size: 1.25rem;
            min-width: 1.5rem;
            text-align: center;
          }
        }
        .price {
          font-size: 0.75rem;
          color: inherit;
          line-height: 1.2;
          text-align: right;
          span {
            color: inherit;
            font-weight: 600;
            font-size: 1.25rem;
          }
        }
      }
      &.active {
        background-color: $bg-white;
        color: $t-primary;

        .bottom {
          .qty {
            .minus,
            .plus {
              background-color: $bg-primary;
            }
          }
        }
      }
    }
  }
}
</style>
